import {
  IPaymentMethodResp,
  MakePaymentForm,
  MakePaymentFormInput,
} from '@monetize/types/app';
import { getCurrencyDecimals } from '@monetize/utils/core';
import { useMemo } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import {
  MCustomNumberInput,
  MFormField,
  MGrid,
  MGridItem,
} from '../../../components/Monetize';
import { DatePicker } from '../../../components/Monetize/DatePicker/DatePicker';
import { MCustomSelect } from '../../../components/Monetize/MCustomSelect/MCustomSelect';
import { PaymentMethodItemRenderer } from '../../../components/PaymentMethods/PaymentMethodItemRenderer';
import { PAYMENT_METHODS } from '../../../constants';
import { getGroupedAndSortedPaymentMethodOptions } from '../../../utils/payment.utils';

type CreatePaymentWithPaymentMethodProps = {
  allPaymentMethods: IPaymentMethodResp[];
  currency: string;
  control: Control<MakePaymentFormInput, unknown, MakePaymentForm>;
  errors: FieldErrors<MakePaymentFormInput>;
  isLoading: boolean;
  isSaving: boolean;
  onOpenPaymentMethodModal: () => void;
};

export const CreatePaymentWithPaymentMethod = ({
  allPaymentMethods,
  currency,
  control,
  errors,
  isLoading,
  isSaving,
  onOpenPaymentMethodModal,
}: CreatePaymentWithPaymentMethodProps) => {
  const paymentMethodItems = useMemo(
    () => getGroupedAndSortedPaymentMethodOptions(allPaymentMethods),
    [allPaymentMethods],
  );

  return (
    <MGrid templateColumns="repeat(2, 1fr)" mt="4" gap={4}>
      <MGridItem colSpan={1}>
        <MFormField error={errors.amount} label="Amount" isRequired>
          <Controller
            name="amount"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <MCustomNumberInput
                precision={getCurrencyDecimals(currency)}
                isDisabled={isSaving}
                inputMode="decimal"
                onChange={(val) => onChange(val)}
                {...field}
                value={value ?? undefined}
              />
            )}
          />
        </MFormField>
      </MGridItem>
      <MGridItem colSpan={1}>
        <MFormField error={errors.paymentDate} label="Payment Date" isRequired>
          <Controller
            name="paymentDate"
            control={control}
            render={({ field }) => (
              <DatePicker isReadOnly isDisabled {...field} />
            )}
          />
        </MFormField>
      </MGridItem>
      <MGridItem colSpan={2}>
        <MFormField error={errors.paymentMethodId} mt="2">
          <Controller
            name="paymentMethodId"
            control={control}
            render={({ field }) => (
              <MCustomSelect
                placeholder="Select Payment Method"
                itemValue="id"
                itemTitle="paymentMethodName"
                loading={isLoading}
                items={paymentMethodItems}
                isDisabled={isSaving}
                prependItems={[
                  {
                    item: PAYMENT_METHODS.NEW_PAYMENT_METHOD,
                    isAction: true,
                    hasDivider: true,
                    onClick: ({ onClose }) => {
                      onClose && onClose();
                      onOpenPaymentMethodModal();
                    },
                  },
                ]}
                renderItemContent={PaymentMethodItemRenderer}
                {...field}
              />
            )}
          />
        </MFormField>
      </MGridItem>
    </MGrid>
  );
};
